import { Alert, Box, Button, Card, Grid, Group, Input, Tabs, Text, TextInput, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Editor } from '@monaco-editor/react';
import DailyLimitSettings from '../DailyLimitSettings';
import LabeledSlider from '../LabeledSlider';
import {
  IconAlertTriangle,
  IconArrowsDiagonal,
  IconArrowsDiagonal2,
  IconCode,
  IconInfoCircle,
  IconPalette,
  IconPhoto,
  IconQuestionMark,
  IconSend,
} from '@tabler/icons-react';
import escapeHTML from 'escape-html';
import { parse } from 'node-html-parser';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import Regex from '../../constants/Regex';
import getMergeTags from '../../constants/getMergeTags';
import useFetch from '../../helpers/useFetch';
import { stripDisallowedTags, HTML_DISALLOWED_TAGS } from '../../helpers/sanitizeEmailHtml';
import AssetList from '../Assets/AssetList';
import StripoEditor from '../Editors/StripoEditor';
import UnlayerEditor from '../Editors/UnlayerEditor';
import ErrorMessage from '../ErrorMessage';
import InvalidRecommenderModal from '../Modals/InvalidRecommenderModal';
import SendTestMessageModal from '../Modals/SendTestMessageModal';
import TemplatesGalleryModal from '../Modals/TemplatesGalleryModal';
import PreviewHTML from '../PreviewHTML';
import Spinner from '../Spinner';
import mergeTagsForEmailEditor from '../mergeTagsForEmailEditor';

const HTML_VIEW_HEIGHT = '40vh';

// Klaviyo delivery carries up to ~900KB per rendered email (chunked event payload); past
// that the send job skips those recipients. The saved template is only a proxy for the
// final size (product blocks expand, click tracking grows the URLs), so warn with margin.
const KLAVIYO_MAX_KB = 850;
// Gmail clips messages above ~102KB in the inbox - independent of the transport.
const GMAIL_CLIP_KB = 102;

const EmailFormInner = ({
  email,
  updateEmail,
  hasName,
  withEmailDesignSaveRef,
  onEditorLoaded,
  tab,
  setTab,
  senderEmailInvalid,
  subjectHandlebarsError,
  previewHandlebarsError,
  senderNameHandlebarsError,
}) => {
  const fetch = useFetch();
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [dynamicFieldsOpen, setDynamicFieldsOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [customFields, setCustomFields] = useState();
  const [testEmailError, setTestEmailError] = useState();
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [templates, setTemplates] = useState();
  const [locales, setLocales] = useState();
  const [error, setError] = useState();
  const [assetModalViewOpen, setAssetModalViewOpen] = useState(false);
  const [productFeeds, setProductFeeds] = useState();
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState();
  const [currencies, setCurrencies] = useState();
  const [invalidRecommenderModalOpen, setInvalidRecommenderModalOpen] = useState(false);
  const stripoEditorRef = useRef(null);
  const unlayerEditorRef = useRef(null);
  const [coupons, setCoupons] = useState();
  const [templatesGalleryModalOpen, setTemplatesGalleryModalOpen] = useState(false);
  const [surveys, setSurveys] = useState();
  const { user } = useSelector((state) => state.auth);

  const { accountId, domainId } = useParams();
  const { domain } = useSelector((state) => state.domain);
  // Loading-aware: a stale account from a previously viewed domain is reset by the
  // slice on refetch, and until this domain's state is known we treat the domain as
  // Releva-managed (fail safe - verification gating stays on).
  const { account: klaviyoAccount, loading: klaviyoLoading } = useSelector((state) => state.klaviyo);
  const klaviyoConnected = klaviyoLoading === false && !!klaviyoAccount;

  // Klaviyo delivery size warning input; memoized - EmailFormInner re-renders per
  // keystroke and Blob() copies/encodes the entire HTML.
  const emailHtmlKb = useMemo(
    () => (klaviyoConnected ? Math.ceil(new Blob([email?.html || '']).size / 1024) : 0),
    [klaviyoConnected, email?.html]
  );

  useEffect(() => {
    setVerificationStatus(undefined);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/verificationStatus`)
      .then((verificationStatus) => {
        setVerificationStatus(verificationStatus);
      })
      .catch(setError);
  }, [accountId, domainId, fetch]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/coupons`)
      .then(({ coupons }) => setCoupons(coupons))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productFeeds`)
      .then(({ productFeeds }) => setProductFeeds(productFeeds.filter((f) => f.scope === 'marketingChannels')))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/locales`)
      .then(({ locales }) => setLocales(locales))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/currencies`)
      .then(({ currencies }) => setCurrencies(currencies))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/templates`)
      .then(({ templates }) => setTemplates(templates.filter((t) => t.scope === 'email')))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setError);
  }, [fetch, accountId, domainId]);

  const handleChange = useCallback(
    (key, value) => {
      updateEmail({ ...email, [key]: value });
    },
    [email, updateEmail]
  );

  const doSend = (email, sendTo, impersonateProfileQuery, triggerEventAction, cb) => {
    setTestEmailSending(true);
    setTestEmailError(false);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails/testEmail`, {
      method: 'POST',
      body: JSON.stringify({
        sendTo,
        impersonateProfileQuery,
        email,
        triggerEventAction,
      }),
    })
      .then(() => {
        cb();
      })
      .catch((testEmailError) => setTestEmailError(testEmailError))
      .finally(() => setTestEmailSending(false));
  };

  const runStripoSave = (cb, ...args) => {
    // `getTemplate` and `compileEmail` are both async. Nest the compile inside
    // the getTemplate callback so `templateData` (the design we persist) is
    // guaranteed captured from the live editor before it is used — otherwise a
    // race could save `design` as `undefined` and wipe the editor content.
    stripoEditorRef.current.getTemplate((templateHtml, css, width, height) => {
      // The design is editor source that round-trips back into Stripo, so we
      // strip with the HTML set (iframe/script/object/embed). This ensures
      // the persisted Stripo design has <script> stripped too. Send-time
      // sanitization on the backend is the real guarantee.
      const cleanDesign = stripDisallowedTags(templateHtml, HTML_DISALLOWED_TAGS);
      const templateData = JSON.stringify({
        html: cleanDesign.html,
        css: css,
        width: width,
        height: height,
      });
      stripoEditorRef.current.compileEmail((error, html) => {
        if (error) {
          setError(error);
        } else {
          // Safety check: if AMP blocks remain draggable in Stripo (plan-dependent),
          // prevent them from shipping as dead unknown elements. Notify rather than
          // setError -- setError replaces the entire form (editor included) and is
          // never cleared, so the user could not act on the message.
          if (html?.toLowerCase().includes('<amp-')) {
            notifications.show({
              color: 'red',
              title: 'AMP elements are not supported',
              message: 'Please remove any AMP-specific blocks from your email and save again.',
            });
            return;
          }
          const cleanHtml = stripDisallowedTags(html, HTML_DISALLOWED_TAGS);
          // The design is the source the compiled HTML is generated from, so a
          // removal usually shows up in both. Take the larger count rather than
          // the sum: it never inflates the number the user sees, and it still
          // surfaces design-only removals (which would otherwise be silent).
          const removedWithContent = Math.max(cleanDesign.removedWithContent, cleanHtml.removedWithContent);
          if (removedWithContent > 0) {
            notifications.show({
              color: 'yellow',
              title: 'Removed embedded content',
              message: `We removed ${removedWithContent} embedded element(s) (e.g. iframes) that email clients block and that hurt deliverability.`,
            });
          }

          // Reuse the tree the helper already built (it preserves Outlook
          // `<!--[if mso]>` comments); fall back to parsing only when the
          // fast path returned no root.
          const parsedHtml = cleanHtml.root ?? parse(cleanHtml.html, { comment: true });
          const blocks = parsedHtml.querySelectorAll('.releva-products');
          for (const block of blocks) {
            // A `.releva-products` element without a `data-releva-config`
            // attribute is an unconfigured product block (e.g. pasted into
            // the editor by hand), not a valid recommender. Treat it the same
            // as a missing feed/template rather than letting JSON.parse throw
            // on `undefined`.
            const config = block.attributes['data-releva-config'];
            if (!config) {
              setInvalidRecommenderModalOpen(true);
              return;
            }
            const { templateId, productFeedId } = JSON.parse(config);
            const productFeed = productFeeds.find((f) => f.id.toString() === productFeedId);
            const template = templates.find((f) => f.id.toString() === templateId);

            if (!productFeed || !template) {
              setInvalidRecommenderModalOpen(true);
              return;
            }
          }

          updateEmail({
            ...email,
            design: templateData,
            html: parsedHtml.outerHTML,
          });
          if (typeof cb === 'function') {
            cb(
              {
                ...email,
                design: templateData,
                html: parsedHtml.outerHTML,
              },
              ...args
            );
          }
        }
      });
    });
  };

  const withEmailDesignSave = (cb, ...args) => {
    if (email.type === 'visual') {
      if (email.editor === 'stripo' && stripoEditorRef.current) {
        // The editor is always kept mounted and rendered (hidden off-screen, never
        // display:none — see the content section render), so getTemplate/compileEmail
        // work reliably regardless of which tab is active.
        runStripoSave(cb, ...args);
      } else if (email.editor === 'unlayer') {
        unlayerEditorRef.current.exportHtml(({ design, html }) => {
          const recommenders = (design?.body?.rows || [])
            .map((row) => {
              return row.columns
                .map((col) => {
                  return col.contents.filter((content) => content.type === 'custom' && content.slug === 'recs');
                })
                .flat();
            })
            .flat();

          if (html) {
            const parsedHtml = parse(html.replace(/table-layout: fixed;/g, ''));
            const blocks = parsedHtml.querySelectorAll(`.recs`);
            for (let i = 0; i < recommenders.length; i++) {
              const recommender = recommenders[i];
              const block = blocks[i];

              const productFeed = productFeeds.find((f) => f.id.toString() === recommender.values.productFeedId);
              const template = templates.find((f) => f.id.toString() === recommender.values.templateId);

              if (productFeed && template) {
                const recommenderConfig = {
                  templateId: template.id.toString(),
                  count: parseInt(recommender.values.count),
                  productFeedId: productFeed.id.toString(),
                };

                block.set_content(`
                  <tbody>
                   <tr>
                     <td style="overflow-wrap:break-word;word-break:break-word;padding:10px;font-family:arial,helvetica,sans-serif;" align="left">
                       <!-- BEGIN product block -->
                       <div class="releva-products" data-releva-config="${escapeHTML(
                         JSON.stringify(recommenderConfig)
                       )}"></div>
                       <!-- END product block -->
                     </td>
                   </tr>
                  </tbody>
                `);
              } else {
                setInvalidRecommenderModalOpen(true);
                return;
              }
            }
            updateEmail({ ...email, design, html: parsedHtml.outerHTML });
            if (typeof cb === 'function') {
              cb({ ...email, design, html: parsedHtml.outerHTML }, ...args);
            }
          } else {
            updateEmail({ ...email, design });
            if (typeof cb === 'function') {
              cb({ ...email, design }, ...args);
            }
          }
        });
      }
    } else {
      if (typeof cb === 'function') {
        cb(email, ...args);
      }
    }
  };

  const send = (recipientEmail, impersonateEmail, triggerEventAction, tracking, cb) => {
    withEmailDesignSave(doSend, recipientEmail, impersonateEmail, triggerEventAction, tracking, cb);
  };

  if (withEmailDesignSaveRef) {
    withEmailDesignSaveRef.current = withEmailDesignSave;
  }

  const mergeTags = getMergeTags(customFields, coupons, surveys);

  let editorComponent;
  let expandCollapseButton;
  let viewAssetsButton;

  editorComponent = useMemo(() => {
    if (
      !email?.type ||
      (email.type === 'visual' && (!email.editor || !email.externalId)) ||
      productFeeds === undefined ||
      templates === undefined ||
      mergeTags === undefined ||
      locales === undefined ||
      currencies === undefined ||
      surveys === undefined
    ) {
      return;
    }

    if (email.type === 'visual') {
      return email.editor === 'stripo' ? (
        <StripoEditor
          mergeTags={mergeTags}
          productFeeds={productFeeds}
          templates={templates}
          surveys={surveys}
          design={email.design}
          externalId={email.externalId}
          stripoEditorRef={stripoEditorRef}
          onStripoLoad={() => {
            setEditorLoaded(true);
            onEditorLoaded();
          }}
        />
      ) : (
        <UnlayerEditor
          editorHeight="40vh"
          design={email.design}
          editorRef={unlayerEditorRef}
          displayMode="email"
          projectId="3137"
          onLoad={() => {
            setEditorLoaded(true);
            onEditorLoaded();
          }}
          mergeTags={mergeTagsForEmailEditor(mergeTags)}
          tools={{
            'custom#recs': {
              properties: {
                productFeedId: {
                  editor: {
                    data: {
                      options: productFeeds
                        .map((feed) => {
                          return { label: feed.name, value: feed.id };
                        })
                        .concat({ label: '', value: '' }),
                    },
                  },
                },
                templateId: {
                  editor: {
                    data: {
                      options: templates
                        .map((t) => {
                          return { label: t.name, value: t.id.toString() };
                        })
                        .concat({ label: '', value: '' }),
                    },
                  },
                },
                locale: {
                  editor: {
                    data: {
                      options: [
                        { label: 'Any', value: null },
                        ...locales.map((l) => {
                          return { label: l, value: l };
                        }),
                      ],
                    },
                  },
                },
                currency: {
                  editor: {
                    data: {
                      options: [
                        { label: 'Any', value: null },
                        ...currencies.map((l) => {
                          return { label: l, value: l };
                        }),
                      ],
                    },
                  },
                },
              },
            },
            menu: {
              enabled: true,
            },
          }}
        />
      );
    } else {
      return (
        <Grid>
          <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 8 : 6 }} className={expanded ? 'FullWidth' : ''}>
            <Grid>
              <Grid.Col
                span={{ base: 12, sm: assetModalViewOpen ? 6 : 12 }}
                display={assetModalViewOpen ? undefined : 'none'}
              >
                <div className="assetsPreviewBox" style={{ overflow: 'auto' }}>
                  <AssetList style={{ height: HTML_VIEW_HEIGHT }} />
                </div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 6 : 12 }}>
                <PreviewHTML height={HTML_VIEW_HEIGHT} htmlPreview={email?.html} />
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 4 : 6 }} className={expanded ? 'FullWidth' : ''}>
            <Editor
              defaultLanguage="html"
              width="100%"
              height={HTML_VIEW_HEIGHT}
              onChange={(value) => handleChange('html', value)}
              name="html"
              value={email.html}
            />
          </Grid.Col>
        </Grid>
      );
    }
  }, [
    assetModalViewOpen,
    currencies,
    email?.type,
    email?.editor,
    email?.html,
    email?.design,
    email?.externalId,
    expanded,
    handleChange,
    locales,
    mergeTags,
    onEditorLoaded,
    templates,
    productFeeds,
    surveys,
  ]);

  if (
    email === undefined ||
    verificationStatus === undefined ||
    customFields === undefined ||
    locales === undefined ||
    templates === undefined ||
    currencies === undefined
  ) {
    return <Spinner />;
  }

  if (email === null) {
    return (
      <Alert color="blue" ta="center">
        <IconAlertTriangle size={16} /> Email has been deleted.
      </Alert>
    );
  }

  const stripoCodeButton =
    email.type === 'visual' && email.editor === 'stripo' ? (
      <Button size="sm" type="button" variant="default" id="codeEditor">
        <IconCode size={14} />
      </Button>
    ) : null;

  let name = '';
  if (hasName === true) {
    name = (
      <Grid mt="xs">
        <Grid.Col span={{ base: 12, sm: 2 }}>
          <Text size="sm" fw={500}>
            Name
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 10 }}>
          <TextInput
            name="name"
            type="text"
            value={email.name}
            placeholder="e.g. Check This Out!"
            onChange={(e) => handleChange(e.target.name, e.target.value)}
          />
        </Grid.Col>
      </Grid>
    );
  }
  if (email.type === 'html') {
    if (expanded) {
      expandCollapseButton = (
        <Button size="sm" type="button" onClick={() => setExpanded(false)}>
          <IconArrowsDiagonal2 size={14} /> Collapse Preview and Editor
        </Button>
      );
    } else {
      expandCollapseButton = (
        <Button size="sm" type="button" onClick={() => setExpanded(true)}>
          <IconArrowsDiagonal size={14} /> Expand Preview and Editor
        </Button>
      );
    }
    if (assetModalViewOpen) {
      viewAssetsButton = (
        <Button size="sm" onClick={() => setAssetModalViewOpen(false)}>
          <IconPhoto size={14} /> Hide Images
        </Button>
      );
    } else {
      viewAssetsButton = (
        <Button size="sm" onClick={() => setAssetModalViewOpen(true)}>
          <IconPhoto size={14} /> Show Images
        </Button>
      );
    }
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  // Thresholds and their rationale live next to KLAVIYO_MAX_KB / GMAIL_CLIP_KB above.
  // Note for `type === 'visual'`: `email.html` is only refreshed by the design-save
  // callback below, so the warning reflects the last saved design, not the one currently
  // being edited in Stripo/Unlayer - it appears (or clears) on save rather than live.
  let klaviyoSizeWarning = null;
  if (klaviyoConnected && emailHtmlKb > KLAVIYO_MAX_KB) {
    klaviyoSizeWarning = (
      <Alert
        color="red"
        icon={<IconAlertTriangle />}
        mb="xs"
        title={`This email is ${emailHtmlKb}KB - too large to send`}
      >
        <Text size="sm">
          Email delivery for this domain goes through Klaviyo, which supports up to ~900KB per email after rendering.
          Sending will fail - reduce the design size (fewer blocks, less inline styling).
        </Text>
      </Alert>
    );
  } else if (klaviyoConnected && emailHtmlKb > GMAIL_CLIP_KB) {
    klaviyoSizeWarning = (
      <Alert color="yellow" icon={<IconAlertTriangle />} mb="xs" title={`This email is ${emailHtmlKb}KB`}>
        <Text size="sm">
          Gmail clips messages larger than ~102KB - recipients see a truncated email with a "View entire message" link,
          and content near the bottom (including the unsubscribe footer) gets hidden. Sending will still work - but
          consider a lighter design.
        </Text>
      </Alert>
    );
  }

  return (
    <>
      {klaviyoSizeWarning}
      <Tabs value={tab} onChange={setTab} mb="md" keepMounted>
        <Tabs.List>
          {email.type !== 'custom' && <Tabs.Tab value="content">Content</Tabs.Tab>}
          {email.type !== 'custom' && <Tabs.Tab value="advanced">Advanced settings</Tabs.Tab>}
        </Tabs.List>
        {email.type === 'custom' ? null : (
          // The Stripo editor lives here and must stay mounted AND rendered even
          // when the Advanced tab is active. We therefore do NOT use Tabs.Panel
          // (which hides inactive panels with display:none) — Stripo resets/breaks
          // its DOM when display:none'd. Instead we keep this content in flow and,
          // when inactive, clip it to zero height (overflow hidden) + mark it inert.
          // The editor's DOM keeps its real dimensions and stays live, so
          // getTemplate/compileEmail work regardless of the active tab.
          <Box
            pt={0}
            role="tabpanel"
            aria-hidden={tab !== 'content'}
            inert={tab !== 'content' ? true : undefined}
            style={tab === 'content' ? undefined : { height: 0, overflow: 'hidden' }}
          >
            <Card withBorder>
              <Card.Section inheritPadding py="sm">
                {email.type === 'custom' && name}
                {email.type === 'custom' ? null : (
                  <>
                    <Box data-tour="emailFormInnerFields">
                      <Grid>
                        <Grid.Col span={{ base: 12, sm: 2 }}>
                          <Text size="sm" fw={500}>
                            Sender Name
                            <Tooltip
                              label="You can change the default sender in your shop settings. Unicode in sender name is supported."
                              position="top"
                            >
                              <IconQuestionMark size={12} />
                            </Tooltip>
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 10 }}>
                          <TextInput
                            name="senderName"
                            type="text"
                            value={email.senderName}
                            placeholder="e.g., Georgi from Releva.ai"
                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                            error={senderNameHandlebarsError}
                          />
                          {klaviyoConnected && (
                            <Text size="sm" c="dimmed">
                              Email delivery is handled by Klaviyo: the sender name comes from your Klaviyo flow message
                              unless the flow references {'{{ event.sender_name }}'}.
                            </Text>
                          )}
                        </Grid.Col>
                      </Grid>
                      {name}
                      <Grid mt="xs">
                        <Grid.Col span={{ base: 12, sm: 2 }}>
                          <Text size="sm" fw={500}>
                            Subject
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 10 }}>
                          <TextInput
                            name="subject"
                            type="text"
                            value={email.subject}
                            placeholder="e.g. Check This Out!"
                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                            error={subjectHandlebarsError}
                          />
                        </Grid.Col>
                      </Grid>
                      <Grid mt="xs">
                        <Grid.Col span={{ base: 12, sm: 2 }}>
                          <Text size="sm" fw={500}>
                            Preview Text
                            <Tooltip
                              label="Preview text appears in the inbox just after the subject line. It does not show in email body."
                              position="top"
                            >
                              <IconQuestionMark size={12} />
                            </Tooltip>
                          </Text>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <TextInput
                            name="preview"
                            type="text"
                            value={email.preview || ''}
                            placeholder="e.g., Check This Out!"
                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                            error={previewHandlebarsError}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, sm: 4 }}>
                          <Group justify="flex-end" gap="xs">
                            {stripoCodeButton}
                            {viewAssetsButton}
                            {expandCollapseButton}
                            {email.type === 'html' && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}
                                aria-expanded={dynamicFieldsOpen}
                              >
                                <IconInfoCircle size={14} /> Merge Tags
                              </Button>
                            )}

                            <Button variant="default" size="sm" onClick={() => setTemplatesGalleryModalOpen(true)}>
                              <IconPalette size={14} /> Template Gallery
                            </Button>
                            <Button
                              color="cyan"
                              size="sm"
                              onClick={() => {
                                setTestEmailOpen(true);
                              }}
                              disabled={!klaviyoConnected && !verificationStatus.isEmailSendingVerified}
                              data-tour="testEmailBtn"
                            >
                              <IconSend size={14} /> Test Email
                            </Button>
                          </Group>
                        </Grid.Col>
                      </Grid>

                      <SendTestMessageModal
                        show={testEmailOpen}
                        send={send}
                        error={testEmailError}
                        sending={testEmailSending}
                        sendingDisabled={email.type === 'visual' && !editorLoaded}
                        close={() => setTestEmailOpen(false)}
                        sendTo={user.email}
                        sendToPlaceholder="john@example.com,jane@doe.com"
                        isSendToValid={(sendTo) => sendTo.split(',').every((email) => Regex.emailRegex.test(email))}
                      />
                    </Box>

                    {email.type === 'html' || editorLoaded ? null : <Spinner />}
                    <Box mt="md" display={email.type === 'html' || editorLoaded ? 'block' : 'none'}>
                      <div data-tour="emailFormInnerVisualContent">{editorComponent}</div>
                    </Box>
                    <InvalidRecommenderModal
                      show={invalidRecommenderModalOpen}
                      close={() => setInvalidRecommenderModalOpen(false)}
                    />
                    <TemplatesGalleryModal
                      show={templatesGalleryModalOpen}
                      close={() => setTemplatesGalleryModalOpen(false)}
                    />
                  </>
                )}
              </Card.Section>
            </Card>
          </Box>
        )}
        {email.type === 'custom' ? null : (
          <Tabs.Panel value="advanced" pt={0}>
            <Card withBorder>
              <Card.Section inheritPadding py="sm">
                <div data-tour="emailFormInnerBehaviorFields">
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      <Text size="sm" fw={500}>
                        Sender Email
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 10 }}>
                      <Input.Wrapper>
                        <TextInput
                          name="senderEmailWithoutDomain"
                          type="text"
                          value={email.senderEmailWithoutDomain || ''}
                          placeholder={domain.senderEmail.split('@')[0]}
                          onChange={(e) => handleChange(e.target.name, e.target.value)}
                          error={senderEmailInvalid}
                          rightSection={<span>@{domain.senderEmail.split('@')[1]}</span>}
                          rightSectionWidth={150}
                        />
                      </Input.Wrapper>
                      {klaviyoConnected ? (
                        <Text size="sm" c="dimmed">
                          Email delivery is handled by Klaviyo: the sender address is the Klaviyo-verified address
                          configured in your Klaviyo flow message and cannot be set from Releva.
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Leaving the field empty means that the email will be send from <b>{domain.replyToEmail}</b>
                        </Text>
                      )}
                    </Grid.Col>
                  </Grid>
                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 2 }}>
                      <Text size="sm" fw={500}>
                        Reply-to Email
                      </Text>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, sm: 10 }}>
                      <TextInput
                        name="replyToEmail"
                        type="text"
                        value={email.replyToEmail || ''}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                        placeholder={domain.replyToEmail}
                      />
                      {klaviyoConnected ? (
                        <Text size="sm" c="dimmed">
                          Email delivery is handled by Klaviyo: the reply-to comes from your Klaviyo flow message unless
                          the flow references {'{{ event.reply_to }}'}.
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          Leaving the field empty means that the Reply To email will be <b>{domain.replyToEmail}</b>
                        </Text>
                      )}
                    </Grid.Col>
                  </Grid>
                  <DailyLimitSettings
                    model={email}
                    onChange={(key, value) => handleChange(key, value)}
                    channelLabel="emails"
                    maxPerDay={domain.maxEmailsPerDay}
                    accountId={accountId}
                    domainId={domainId}
                    settingsTab="emails"
                  />
                </div>
              </Card.Section>
            </Card>
          </Tabs.Panel>
        )}
      </Tabs>
    </>
  );
};

export default EmailFormInner;
